import React from "react";
import dayjs from "dayjs";

/**
 * Helper utility to validate mandatory supporting documents for Employee (Driver) and Vehicle Master
 * based on category (Authorized, Ad Hoc, Ad Hoc Special) .
 * 
 * Strictly checks the File Classification Dropdown selection.
 */

export const EMPLOYEE_AUTH_STATUS = {
  AUTHORIZED: 1,
  AD_HOC: 3,
  AD_HOC_SPECIAL: 4,
};

export const VEHICLE_OPERATIONAL_STATUS = {
  AUTHORIZED: 1,
  AD_HOC: 3,
  AD_HOC_SPECIAL: 4,
};

/**
 * Calculate age from birth date string (YYYY-MM-DD) or Date object
 */
export const calculateAgeFromDob = (birthDay) => {
  if (!birthDay) return "";
  const today = new Date();
  const birthDate = new Date(birthDay);
  let calculatedAge = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    calculatedAge--;
  }
  return calculatedAge < 0 ? 0 : calculatedAge;
};

/**
 * Calculate Medical Certificate Expiry Date as Issue Date + 2 years (YYYY-MM-DD)
 */
export const calculateMedicalExpiryDate = (issueDate) => {
  if (!issueDate) return "";
  const d = dayjs(issueDate);
  if (!d.isValid()) return "";
  return d.add(2, "year").format("YYYY-MM-DD");
};

/**
 * Check if a Medical Certificate is valid based on its issue date.
 * Valid until Issue Date + 2 years (expired on/after the expiry date).
 */
export const isMedicalCertificateValid = (issueDate) => {
  if (!issueDate) return false;
  const expiryDate = calculateMedicalExpiryDate(issueDate);
  if (!expiryDate) return false;
  const today = dayjs().format("YYYY-MM-DD");
  return today < expiryDate;
};

/**
 * Check if Medical Certificate is required for a Driver (Category 1, not Ad Hoc Special, Age >= 60)
 */
export const isMedicalCertificateRequiredForDriver = (
  empCategory,
  empAuthorized,
  empBirthDateOrAge
) => {
  if (Number(empCategory) !== 1) return false;
  const authStatus = Number(empAuthorized);
  if (authStatus === EMPLOYEE_AUTH_STATUS.AD_HOC_SPECIAL || !authStatus) return false;

  let age = empBirthDateOrAge;
  if (typeof empBirthDateOrAge === "string" && empBirthDateOrAge.includes("-")) {
    age = calculateAgeFromDob(empBirthDateOrAge);
  }
  return age !== "" && age !== null && age !== undefined && Number(age) >= 60;
};

// Check if Driver documents are mandatory
export const isDriverDocumentMandatory = (empCategory, empAuthorized) => {
  // Category 1 is Driver
  if (Number(empCategory) !== 1) {
    return false;
  }
  const authStatus = Number(empAuthorized);
  return authStatus === EMPLOYEE_AUTH_STATUS.AUTHORIZED || authStatus === EMPLOYEE_AUTH_STATUS.AD_HOC;
};

// Check if Vehicle documents are mandatory
export const isVehicleDocumentMandatory = (vehOperationalStatus) => {
  const status = Number(vehOperationalStatus);
  return status === VEHICLE_OPERATIONAL_STATUS.AUTHORIZED || status === VEHICLE_OPERATIONAL_STATUS.AD_HOC;
};

/**
 * Check if a specific file classification option is required for a Driver
 */
export const isClassificationRequiredForDriver = (
  optText = "",
  empAuthorized,
  empCategory,
  empBirthDateOrAge = null
) => {
  if (Number(empCategory) !== 1) return false;
  const status = Number(empAuthorized);
  if (status === EMPLOYEE_AUTH_STATUS.AD_HOC_SPECIAL || !status) return false;

  const text = String(optText).trim();

  // NIC
  if (/nic|national\s*id/i.test(text)) return true;
  // Driving License
  if (/driv.*licen|dl\b/i.test(text)) return true;
  // Employee Image / Photograph (Explicitly excludes generic "personal" / "personal details")
  if (/photo|photograph|driver\s*image|emp\s*photo|emp.*image|employee\s*image/i.test(text)) return true;

  if (status === EMPLOYEE_AUTH_STATUS.AUTHORIZED) {
    // Police Report (PR)
    if (/police|clearance|pr\b/i.test(text)) return true;
    // Grama Sevaka (GS)
    if (/grama|sevaka|gs\b/i.test(text)) return true;
  }

  // Medical Certificate mandatory for drivers aged 60 and above
  if (isMedicalCertificateRequiredForDriver(empCategory, empAuthorized, empBirthDateOrAge)) {
    if (/medic|medical|med.*cert|health.*cert/i.test(text)) return true;
  }

  return false;
};

/**
 * Check if a specific file classification option is required for a Vehicle
 */
export const isClassificationRequiredForVehicle = (
  optText = "",
  vehOperationalStatus
) => {
  const status = Number(vehOperationalStatus);
  if (status === VEHICLE_OPERATIONAL_STATUS.AD_HOC_SPECIAL || !status) return false;

  const text = String(optText).trim();

  // Vehicle Registration
  if (/registration|reg\b|vr\b|cr\b|book\s*reg/i.test(text)) return true;
  // Revenue License
  if (/revenue|revenue\s*licen|rl\b/i.test(text)) return true;
  // Vehicle Insurance
  if (/insurance|ins\b/i.test(text)) return true;
  // Vehicle Photographs (Front Image is mandatory; Back Image, Side Left, Side Right are not mandatory)
  if (/front/i.test(text)) return true;

  if (status === VEHICLE_OPERATIONAL_STATUS.AUTHORIZED) {
    // EFL Certification (Strictly EFL Certification; excludes Fitness / Green Test)
    if (/efl|efl\s*cert/i.test(text)) return true;
  }

  return false;
};

/**
 * Decorates fileNameOptions by appending a small red star to mandatory classifications
 */
export const getDecoratedFileNameOptions = (
  fileNameOptions = [],
  { opeType, empCategory, empAuthorized, vehOperationalStatus, empBirthDate, empAge }
) => {
  if (!Array.isArray(fileNameOptions)) return [];

  return fileNameOptions.map((opt) => {
    let isMandatory = false;
    const optText = opt.text || opt.title || opt.keyValue1 || "";

    if (Number(opeType) === 9) {
      isMandatory = isClassificationRequiredForDriver(
        optText,
        empAuthorized,
        empCategory,
        empAge !== undefined && empAge !== "" && empAge !== null ? empAge : empBirthDate
      );
    } else if (Number(opeType) === 4) {
      isMandatory = isClassificationRequiredForVehicle(
        optText,
        vehOperationalStatus
      );
    }

    if (isMandatory) {
      return {
        ...opt,
        isMandatoryDoc: true,
        content: (
          <span>
            {optText}
            <span
              style={{
                color: "#db2828",
                fontWeight: "bold",
                marginLeft: "4px",
              }}
            >
              *
            </span>
          </span>
        ),
      };
    }

    return {
      ...opt,
      isMandatoryDoc: false,
      content: optText,
    };
  });
};

/**
 * Get classification text strictly from the selected File Classification dropdown
 * (Explicitly ignores custom document name text inputs)
 */
const getSelectedClassificationText = (doc, fileNameOptions = []) => {
  if (!doc) return "";
  if (doc.fileName != null && Array.isArray(fileNameOptions)) {
    const opt = fileNameOptions.find(
      (o) =>
        String(o.value) === String(doc.fileName) ||
        String(o.key) === String(doc.fileName) ||
        String(o.keyId) === String(doc.fileName) ||
        String(o.keyNo) === String(doc.fileName) ||
        String(o.id) === String(doc.fileName)
    );
    if (opt) {
      return opt.text || opt.title || opt.keyValue1 || opt.keyVal1 || opt.name || "";
    }
  }
  if (doc.fileNameValue) {
    return doc.fileNameValue;
  }
  return "";
};

/**
 * Validate Driver Documents - Strictly evaluates File Classification dropdown selections
 * @param {Array|object} uploadedDocs - List of uploaded document objects
 * @param {number|string} empAuthorized - 1: Authorized, 3: Ad Hoc, 4: Ad Hoc Special
 * @param {number|string} empCategory - 1: Driver
 * @param {Array} fileNameOptions - Dropdown options for classification
 * @param {string|number} empBirthDateOrAge - Driver date of birth or calculated age
 * @param {string} empMedicalIssueDate - Medical certificate issue date
 * @returns { isValid: boolean, missingDocs: string[], errors: object }
 */
export const validateDriverDocuments = (
  uploadedDocs = [],
  empAuthorized,
  empCategory,
  fileNameOptions = [],
  empBirthDateOrAge = null,
  empMedicalIssueDate = null
) => {
  if (Number(empCategory) !== 1) {
    return { isValid: true, missingDocs: [], errors: {} };
  }

  const authStatus = Number(empAuthorized);
  if (authStatus === EMPLOYEE_AUTH_STATUS.AD_HOC_SPECIAL || !authStatus) {
    return { isValid: true, missingDocs: [], errors: {} };
  }

  let docs = [];
  if (Array.isArray(uploadedDocs)) {
    docs = uploadedDocs;
  } else if (uploadedDocs && typeof uploadedDocs === "object") {
    docs = [
      ...(uploadedDocs.created || []),
      ...(uploadedDocs.modified || []),
      ...(uploadedDocs.unmodified || []),
    ];
  }

  const selectedTexts = docs
    .map((d) => getSelectedClassificationText(d, fileNameOptions))
    .filter(Boolean);

  const missingDocs = [];
  const errors = {};

  // Check NIC (Front & Back if distinct options exist, else generic)
  const hasNicFrontOpt = fileNameOptions.some((o) => /nic.*front|front.*nic/i.test(o.text || o.keyValue1 || ""));
  const hasNicBackOpt = fileNameOptions.some((o) => /nic.*back|back.*nic/i.test(o.text || o.keyValue1 || ""));

  if (hasNicFrontOpt && hasNicBackOpt) {
    if (!selectedTexts.some((t) => /nic.*front|front.*nic/i.test(t))) {
      missingDocs.push("Employee NIC – Front");
    }
    if (!selectedTexts.some((t) => /nic.*back|back.*nic/i.test(t))) {
      missingDocs.push("Employee NIC – Back");
    }
  } else {
    if (!selectedTexts.some((t) => /nic|national\s*id/i.test(t))) {
      missingDocs.push("Employee NIC – Front & Back");
    }
  }

  // Check Driving License (Front & Back if distinct options exist, else generic)
  const hasDlFrontOpt = fileNameOptions.some((o) => /driv.*licen.*front|dl.*front|licen.*front/i.test(o.text || o.keyValue1 || ""));
  const hasDlBackOpt = fileNameOptions.some((o) => /driv.*licen.*back|dl.*back|licen.*back/i.test(o.text || o.keyValue1 || ""));

  if (hasDlFrontOpt && hasDlBackOpt) {
    if (!selectedTexts.some((t) => /driv.*licen.*front|dl.*front|licen.*front/i.test(t))) {
      missingDocs.push("Driving License – Front");
    }
    if (!selectedTexts.some((t) => /driv.*licen.*back|dl.*back|licen.*back/i.test(t))) {
      missingDocs.push("Driving License – Back");
    }
  } else {
    if (!selectedTexts.some((t) => /driv.*licen|dl\b/i.test(t))) {
      missingDocs.push("Driving License – Front & Back");
    }
  }

  // Check Employee Image (Strictly photo/photograph/image; excludes "personal details")
  if (!selectedTexts.some((t) => /photo|photograph|driver\s*image|emp\s*photo|emp.*image|employee\s*image/i.test(t))) {
    missingDocs.push("Employee Image");
  }

  if (authStatus === EMPLOYEE_AUTH_STATUS.AUTHORIZED) {
    // Check Police Report (PR)
    if (!selectedTexts.some((t) => /police|clearance|pr\b/i.test(t))) {
      missingDocs.push("Police Report (PR)");
    }

    // Check Grama Sevaka (GS) (Front & Back if distinct options exist, else generic)
    const hasGsFrontOpt = fileNameOptions.some((o) => /gs.*front|grama.*front|front.*gs/i.test(o.text || o.keyValue1 || ""));
    const hasGsBackOpt = fileNameOptions.some((o) => /gs.*back|grama.*back|back.*gs/i.test(o.text || o.keyValue1 || ""));

    if (hasGsFrontOpt && hasGsBackOpt) {
      if (!selectedTexts.some((t) => /gs.*front|grama.*front|front.*gs/i.test(t))) {
        missingDocs.push("GS – Front");
      }
      if (!selectedTexts.some((t) => /gs.*back|grama.*back|back.*gs/i.test(t))) {
        missingDocs.push("GS – Back");
      }
    } else {
      if (!selectedTexts.some((t) => /grama|sevaka|gs\b/i.test(t))) {
        missingDocs.push("GS – Front & Back");
      }
    }
  }

  // Check Medical Certificate for drivers aged 60 and above
  if (isMedicalCertificateRequiredForDriver(empCategory, empAuthorized, empBirthDateOrAge)) {
    const medicalOpt = Array.isArray(fileNameOptions)
      ? fileNameOptions.find((o) =>
          /medic|medical|med.*cert|health.*cert/i.test(o.text || o.title || o.keyValue1 || o.keyVal1 || o.name || "")
        )
      : null;
    const medicalLabel =
      (medicalOpt && (medicalOpt.text || medicalOpt.title || medicalOpt.keyValue1 || medicalOpt.keyVal1 || medicalOpt.name)) ||
      "Medical Certificate";

    const hasMedicalDoc = selectedTexts.some((t) =>
      /medic|medical|med.*cert|health.*cert/i.test(t)
    );
    if (!hasMedicalDoc) {
      missingDocs.push(medicalLabel);
    } else if (empMedicalIssueDate && !isMedicalCertificateValid(empMedicalIssueDate)) {
      errors.medicalCertificateExpired = "Medical Certificate has expired. An expired Medical Certificate cannot satisfy the mandatory document requirement.";
    }
  }

  const isValid = missingDocs.length === 0 && !errors.medicalCertificateExpired;
  if (!isValid) {
    const docErrors = [];
    if (missingDocs.length > 0) {
      docErrors.push(`Missing mandatory documents: ${missingDocs.join(", ")}`);
    }
    if (errors.medicalCertificateExpired) {
      docErrors.push(errors.medicalCertificateExpired);
    }
    errors.documentUploadErrors = docErrors.join(" | ");
  }

  return { isValid, missingDocs, errors };
};

/**
 * Validate Vehicle Documents - Strictly evaluates File Classification dropdown selections
 * @param {Array|object} uploadedDocs - List of uploaded document objects
 * @param {number|string} vehOperationalStatus - 1: Authorized, 3: Ad Hoc, 4: Ad Hoc Special
 * @param {Array} fileNameOptions - Dropdown options for classification
 * @returns { isValid: boolean, missingDocs: string[], errors: object }
 */
export const validateVehicleDocuments = (
  uploadedDocs = [],
  vehOperationalStatus,
  fileNameOptions = []
) => {
  const status = Number(vehOperationalStatus);
  if (status === VEHICLE_OPERATIONAL_STATUS.AD_HOC_SPECIAL || !status) {
    return { isValid: true, missingDocs: [], errors: {} };
  }

  let docs = [];
  if (Array.isArray(uploadedDocs)) {
    docs = uploadedDocs;
  } else if (uploadedDocs && typeof uploadedDocs === "object") {
    docs = [
      ...(uploadedDocs.created || []),
      ...(uploadedDocs.modified || []),
      ...(uploadedDocs.unmodified || []),
    ];
  }

  const selectedTexts = docs
    .map((d) => getSelectedClassificationText(d, fileNameOptions))
    .filter(Boolean);

  const missingDocs = [];

  // Vehicle Registration
  if (!selectedTexts.some((t) => /registration|reg\b|vr\b|cr\b|book\s*reg/i.test(t))) {
    missingDocs.push("Vehicle Registration");
  }

  // Revenue License
  if (!selectedTexts.some((t) => /revenue|revenue\s*licen|rl\b/i.test(t))) {
    missingDocs.push("Revenue License");
  }

  // Vehicle Insurance
  if (!selectedTexts.some((t) => /insurance|ins\b/i.test(t))) {
    missingDocs.push("Vehicle Insurance");
  }

  // Vehicle Photographs (Front Image is mandatory; Back Image, Side Left, Side Right are not mandatory)
  const hasFrontOpt = fileNameOptions.some((o) => /front/i.test(o.text || o.keyValue1 || ""));

  if (hasFrontOpt && !selectedTexts.some((t) => /front/i.test(t))) {
    missingDocs.push("Vehicle Photographs: Front");
  }

  // If specific Front Image option wasn't in the options list, check generic photo
  if (!hasFrontOpt) {
    if (!selectedTexts.some((t) => /photo|photograph|image|veh.*photo/i.test(t))) {
      missingDocs.push("Vehicle Photographs");
    }
  }

  // Authorized Vehicle: EFL Certification (Strictly EFL Certification; excludes Fitness / Green Test)
  if (status === VEHICLE_OPERATIONAL_STATUS.AUTHORIZED) {
    const hasEfl = selectedTexts.some((t) => /efl|efl\s*cert/i.test(t));
    if (!hasEfl) {
      missingDocs.push("EFL Certification");
    }
  }

  const isValid = missingDocs.length === 0;
  const errors = {};
  if (!isValid) {
    errors.documentUploadErrors = `Missing mandatory documents: ${missingDocs.join(", ")}`;
  }

  return { isValid, missingDocs, errors };
};
